import React, { ReactElement } from 'react'
import { impressionEventConfiguration } from '@scentregroup/shared/advertising'
import {
  ColorCards,
  ColorCard,
} from '@scentregroup/shared/hub-components/color-cards'
import { AsMeasured } from '@scentregroup/shared/hub-components/as-measured'
import ImpressionTracker from '@scentregroup/shared/hub-components/impression-tracker'
import { transformContentClickedItemsProperties } from '@scentregroup/shared/analytics-segment/events/content-item'
import { useAnalytics } from '@scentregroup/shared/analytics'
import { AnalyticsImpressionTracker } from '@scentregroup/shared/analytics-observer'
import {
  SGPageTemplateContentCollection,
  SGPageTemplateContentEntry,
} from '@scentregroup/shared/types/page-templates'
import { ModuleProps } from '../../types'

export default function SGPageTemplateContentCollectionColourCardsModule({
  module,
  enableH1Heading: enableH1Heading,
}: ModuleProps<SGPageTemplateContentCollection>): JSX.Element | null {
  const { trackEvent } = useAnalytics()
  const items = React.useMemo(
    () =>
      module.items.filter(
        (item): item is SGPageTemplateContentEntry =>
          item.__typename === 'SGPageTemplateContentEntry'
      ),
    [module.items]
  )
  const ColorCardsAs = React.useMemo(() => {
    return (asProps: Parameters<typeof AsMeasured>[0]) => (
      <AsMeasured
        {...asProps}
        moduleKind={module.kind}
        moduleMetadata={module.analyticsMetadata}
      />
    )
  }, [module.analyticsMetadata, module.kind])

  const AnalyticsImpressionTrackerAs = React.useMemo(() => {
    return items.map(item => (props: Parameters<typeof AsMeasured>[0]) => (
      <AsMeasured
        {...props}
        metadata={item.analyticsMetadata}
        slot={module.analyticsMetadata?.displayId}
      />
    ))
  }, [items, module.analyticsMetadata?.displayId])

  if (items.length === 0) {
    return null
  }

  const TAG_PROMOTED = 'Promoted'

  const handleImpression = (impressionUrl: string): void => {
    fetch(impressionUrl)
  }
  const triggerOnClick = (index: number): void => {
    trackEvent({
      object: 'Content',
      action: 'Clicked',
      properties: transformContentClickedItemsProperties(
        { ...module, items },
        index
      ),
    })
  }

  return (
    <ColorCards
      as={ColorCardsAs}
      title={module.title ?? undefined}
      enableH1Heading={enableH1Heading}
    >
      {items.map((item, idx): ReactElement | undefined =>
        (item.adConfig?.impressionEvents || []).reduce(
          (aggregate, current): ReactElement => {
            const { eventName, eventUrl } = current ?? {}
            if (!eventName || !eventUrl) {
              return aggregate
            }

            const impressionConfig = impressionEventConfiguration[eventName]
            if (!impressionConfig) {
              return aggregate
            }

            return (
              <ImpressionTracker
                key={`${idx}-${eventName}`}
                style={{ display: 'grid' }}
                timeToImpression={impressionConfig.time}
                options={{ threshold: impressionConfig.viewableThreshold }}
                onCreateImpression={() => {
                  handleImpression(eventUrl)
                }}
              >
                {aggregate}
              </ImpressionTracker>
            )
          },
          <AnalyticsImpressionTracker
            as={AnalyticsImpressionTrackerAs[idx]}
            child={ColorCard}
            key={idx}
            {...item}
            {...(item?.adConfig ? { tag: TAG_PROMOTED } : {})}
            clickEvent={triggerOnClick}
          />
        )
      )}
    </ColorCards>
  )
}
